import React, { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Table } from '../components/ui/Table';
import { DatePicker } from '../components/ui/DatePicker';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { callBillingAPI } from '../utils/api';
import { formatCurrency } from '../utils/formatters';
import { PlusIcon, SearchIcon, EyeIcon, EditIcon, AlertCircleIcon, FilterIcon, Share2Icon, ChevronLeftIcon, ChevronRightIcon, RefreshCwIcon, SendIcon } from 'lucide-react';
// Storage key for persisting invoice filters
export const INVOICE_FILTERS_STORAGE_KEY = 'invoiceFilters';
// API invoice type
interface ApiInvoice {
  id: string;
  number: string;
  status: string;
  customer_id: string;
  customer_name: string;
  amount: number;
  invoice_date?: number; // optional, unix timestamp
  hash: string; // hash for public URL (legacy)
  link?: string; // absolute link to public invoice
}
// Display invoice type
interface Invoice {
  id: string;
  number: string;
  status: string;
  customer_id: string;
  customer_name: string;
  amount: number;
  invoice_date: Date | null;
  hash: string; // hash for public URL (legacy)
  link: string | null; // absolute link to public invoice
}
export function Invoices() {
  const {
    selectedCompanyId
  } = useData();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // Data state
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  // Get filter values from URL search params
  const statusFilter = searchParams.get('status') || 'all';
  const searchQuery = searchParams.get('search') || '';
  const startDate = searchParams.get('from') || '';
  const endDate = searchParams.get('to') || '';
  const pageParam = searchParams.get('page');
  // Local state for search input (for debounce)
  const [searchInput, setSearchInput] = useState(searchQuery);
  const [showFilters, setShowFilters] = useState(!!startDate || !!endDate);
  // Share modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareInvoiceLink, setShareInvoiceLink] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  // Send invoice state
  const [sendingInvoiceId, setSendingInvoiceId] = useState<string | null>(null);
  // Save current filters to sessionStorage whenever they change
  useEffect(() => {
    const currentFilters = searchParams.toString();
    if (currentFilters) {
      sessionStorage.setItem(INVOICE_FILTERS_STORAGE_KEY, currentFilters);
    } else {
      sessionStorage.removeItem(INVOICE_FILTERS_STORAGE_KEY);
    }
  }, [searchParams]);
  // Helper function to update URL search params
  const updateSearchParams = (updates: Record<string, string | null>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === '' || value === 'all') {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    // Reset page to 1 when filters change (except when explicitly setting page)
    if (!('page' in updates)) {
      newParams.delete('page');
    }
    setSearchParams(newParams);
  };
  // Apply filter from navigation state (from Dashboard clicks)
  useEffect(() => {
    const state = location.state as {
      filterStatus?: string;
    } | null;
    if (state?.filterStatus) {
      updateSearchParams({
        status: state.filterStatus
      });
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  // Sync search input with URL param on mount
  useEffect(() => {
    setSearchInput(searchQuery);
  }, []);
  // Fetch invoices from API
  const fetchInvoices = async (page: number = 1, search: string = '', status: string = 'all', fromDate: string = '', toDate: string = '') => {
    if (!selectedCompanyId) return;
    setIsLoading(true);
    setError(null);
    try {
      const params: Record<string, any> = {
        company_id: selectedCompanyId,
        page: page
      };
      if (search.trim()) {
        params.search = search.trim();
      }
      if (status !== 'all') {
        params.status = status;
      }
      // Convert date from yyyy-mm-dd (input format) to mm-dd-yyyy (API format)
      if (fromDate) {
        const date = new Date(fromDate);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        params.from_date = `${month}-${day}-${year}`;
      }
      if (toDate) {
        const date = new Date(toDate);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        params.to_date = `${month}-${day}-${year}`;
      }
      console.log('Fetching invoices with params:', params);
      const response = await callBillingAPI('getInvoices', params);
      console.log('Invoices API response:', response);
      // Transform API invoices
      const apiInvoices: ApiInvoice[] = response.items || [];
      const transformedInvoices: Invoice[] = apiInvoices.map(inv => ({
        id: inv.id,
        number: inv.number,
        status: inv.status,
        customer_id: inv.customer_id,
        customer_name: inv.customer_name,
        amount: inv.amount,
        invoice_date: inv.invoice_date ? new Date(inv.invoice_date * 1000) : null,
        hash: inv.hash,
        link: inv.link || null
      }));
      setInvoices(transformedInvoices);
      setTotalCount(response.total_count || 0);
      setTotalPages(response.total_pages || 0);
      setHasNextPage(response.has_next_page || false);
      setCurrentPage(page);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
      setError(err instanceof Error ? err.message : 'Failed to load invoices');
      setInvoices([]);
    } finally {
      setIsLoading(false);
    }
  };
  // Fetch on mount and when company/filters change (from URL params)
  useEffect(() => {
    const page = pageParam ? parseInt(pageParam) : 1;
    fetchInvoices(page, searchQuery, statusFilter, startDate, endDate);
  }, [selectedCompanyId, statusFilter, startDate, endDate, searchQuery, pageParam]);
  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== searchQuery) {
        updateSearchParams({
          search: searchInput || null
        });
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);
  const handleStatusChange = (newStatus: string) => {
    updateSearchParams({
      status: newStatus
    });
  };
  const handleStartDateChange = (newDate: string) => {
    updateSearchParams({
      from: newDate || null
    });
    if (newDate) setShowFilters(true);
  };
  const handleEndDateChange = (newDate: string) => {
    updateSearchParams({
      to: newDate || null
    });
    if (newDate) setShowFilters(true);
  };
  const handleNextPage = () => {
    if (hasNextPage) {
      updateSearchParams({
        page: String(currentPage + 1)
      });
    }
  };
  const handlePrevPage = () => {
    if (currentPage > 1) {
      updateSearchParams({
        page: currentPage > 2 ? String(currentPage - 1) : null
      });
    }
  };
  const handleRefresh = () => {
    const page = pageParam ? parseInt(pageParam) : 1;
    fetchInvoices(page, searchQuery, statusFilter, startDate, endDate);
  };
  const handleClearFilters = () => {
    setSearchInput('');
    setSearchParams(new URLSearchParams());
  };
  const handleShareInvoice = (invoice: Invoice) => {
    // Use the link field from API, fallback to constructing from hash
    const shareUrl = invoice.link || `${window.location.origin}/public/invoice/${invoice.hash}`;
    setShareInvoiceLink(shareUrl);
    setShareModalOpen(true);
    setCopySuccess(false);
  };
  const handleCopyLink = async () => {
    if (!shareInvoiceLink) return;
    try {
      await navigator.clipboard.writeText(shareInvoiceLink);
      setCopySuccess(true);
      setTimeout(() => {
        setShareModalOpen(false);
        setCopySuccess(false);
      }, 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Please copy this link manually: ' + shareInvoiceLink);
    }
  };
  const handleViewLink = () => {
    if (!shareInvoiceLink) return;
    window.open(shareInvoiceLink, '_blank');
    setShareModalOpen(false);
  };
  // Handle send invoice
  const handleSendInvoice = async (invoice: Invoice) => {
    setSendingInvoiceId(invoice.id);
    try {
      await callBillingAPI('sendInvoice', {
        id: invoice.id
      });
      // Refresh the list after sending
      const page = pageParam ? parseInt(pageParam) : 1;
      await fetchInvoices(page, searchQuery, statusFilter, startDate, endDate);
    } catch (err) {
      console.error('Failed to send invoice:', err);
      alert(err instanceof Error ? err.message : 'Failed to send invoice');
    } finally {
      setSendingInvoiceId(null);
    }
  };
  // Get status badge
  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, {
      variant: 'success' | 'warning' | 'danger' | 'info' | 'default';
      label: string;
    }> = {
      draft: {
        variant: 'default',
        label: 'Draft'
      },
      sent: {
        variant: 'info',
        label: 'Sent'
      },
      paid: {
        variant: 'success',
        label: 'Paid'
      },
      partial_paid: {
        variant: 'warning',
        label: 'Partial'
      },
      overdue: {
        variant: 'danger',
        label: 'Overdue'
      },
      voided: {
        variant: 'default',
        label: 'Voided'
      }
    };
    const config = statusConfig[status] || {
      variant: 'default' as const,
      label: status
    };
    return <Badge variant={config.variant} data-id="element-1327">{config.label}</Badge>;
  };
  // Format date
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const statusOptions = [{
    value: 'all',
    label: 'All Statuses'
  }, {
    value: 'draft',
    label: 'Draft'
  }, {
    value: 'sent',
    label: 'Sent'
  }, {
    value: 'paid',
    label: 'Paid'
  }, {
    value: 'partial_paid',
    label: 'Partial Paid'
  }, {
    value: 'overdue',
    label: 'Overdue'
  }, {
    value: 'voided',
    label: 'Voided'
  }];
  // Check if any filters are active
  const hasActiveFilters = statusFilter !== 'all' || searchQuery || startDate || endDate;
  const columns = [{
    key: 'number',
    header: 'Invoice',
    render: (invoice: Invoice) => {
      const isEditable = invoice.status === 'draft';
      return <Link to={isEditable ? `/invoices/${invoice.id}/edit` : `/invoices/${invoice.id}`} className="text-blue-600 hover:text-blue-800 font-medium" data-id="element-1328">
            {invoice.number || invoice.id.slice(0, 8)}
          </Link>;
    }
  }, {
    key: 'customer',
    header: 'Customer',
    render: (invoice: Invoice) => <span className="font-medium" data-id="element-1329">
          {invoice.customer_name || 'Unknown'}
        </span>
  }, {
    key: 'invoice_date',
    header: 'Invoice Date',
    render: (invoice: Invoice) => <span className={invoice.invoice_date ? '' : 'text-gray-400'} data-id="element-1330">
          {invoice.invoice_date ? formatDate(invoice.invoice_date) : '—'}
        </span>
  }, {
    key: 'amount',
    header: 'Amount',
    render: (invoice: Invoice) => <span className="font-semibold" data-id="element-1331">{formatCurrency(invoice.amount)}</span>
  }, {
    key: 'status',
    header: 'Status',
    render: (invoice: Invoice) => getStatusBadge(invoice.status)
  }, {
    key: 'actions',
    header: '',
    render: (invoice: Invoice) => {
      const isEditable = invoice.status === 'draft';
      const isDraft = invoice.status === 'draft';
      const isSending = sendingInvoiceId === invoice.id;
      return <div className="flex items-center justify-end gap-1" data-id="element-1332">
            {isDraft && <Button variant="ghost" size="sm" onClick={e => {
          e.stopPropagation();
          handleSendInvoice(invoice);
        }} disabled={isSending} className="text-gray-600 hover:text-green-600 hover:bg-green-50" aria-label="Send invoice" title="Send invoice" data-id="element-1333">
                {isSending ? <RefreshCwIcon className="w-4 h-4 animate-spin" data-id="element-1334" /> : <SendIcon className="w-4 h-4" data-id="element-1335" />}
              </Button>}
            <Button variant="ghost" size="sm" onClick={e => {
          e.stopPropagation();
          handleShareInvoice(invoice);
        }} className="text-gray-600 hover:text-blue-600 hover:bg-blue-50" aria-label="Share public link" title="Share public view" data-id="element-1336">
              <Share2Icon className="w-4 h-4" data-id="element-1337" />
            </Button>
            <Link to={isEditable ? `/invoices/${invoice.id}/edit` : `/invoices/${invoice.id}`} data-id="element-1338">
              <Button variant="ghost" size="sm" aria-label={isEditable ? 'Edit invoice' : 'View invoice'} data-id="element-1339">
                {isEditable ? <EditIcon className="w-4 h-4" data-id="element-1340" /> : <EyeIcon className="w-4 h-4" data-id="element-1341" />}
              </Button>
            </Link>
          </div>;
    }
  }];
  return <div className="space-y-6" data-id="element-1342">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-1343">
        <div data-id="element-1344">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1345">Invoices</h1>
          <p className="text-gray-500 mt-1" data-id="element-1346">
            {totalCount} invoice{totalCount !== 1 ? 's' : ''} total
          </p>
        </div>
        <div className="flex gap-2" data-id="element-1347">
          <Button variant="secondary" onClick={handleRefresh} disabled={isLoading} leftIcon={<RefreshCwIcon className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} data-id="element-1349" />} data-id="element-1348">
            Refresh
          </Button>
          <Link to="/invoices/new" data-id="element-1350">
            <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1352" />} data-id="element-1351">
              New Invoice
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <Card data-id="element-1353">
        <div className="space-y-4" data-id="element-1354">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-1355">
            <div className="flex-1 relative" data-id="element-1356">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1357" />
              <Input placeholder="Invoice number or customer" value={searchInput} onChange={e => setSearchInput(e.target.value)} className="pl-10" data-id="element-1358" />
            </div>
            <div className="w-full sm:w-48" data-id="element-1359">
              <Select options={statusOptions} value={statusFilter} onChange={e => handleStatusChange(e.target.value)} data-id="element-1360" />
            </div>
            <Button variant="secondary" onClick={() => setShowFilters(!showFilters)} leftIcon={<FilterIcon className="w-4 h-4" data-id="element-1362" />} data-id="element-1361">
              {showFilters ? 'Hide Filters' : 'More Filters'}
            </Button>
          </div>

          {showFilters && <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-gray-200" data-id="element-1363">
              <div className="w-full sm:w-48" data-id="element-1364">
                <DatePicker label="From Date" value={startDate} onChange={e => handleStartDateChange(e.target.value)} data-id="element-1365" />
              </div>
              <div className="w-full sm:w-48" data-id="element-1366">
                <DatePicker label="To Date" value={endDate} onChange={e => handleEndDateChange(e.target.value)} data-id="element-1367" />
              </div>
            </div>}

          {hasActiveFilters && <div data-id="element-1368">
              <Button variant="ghost" onClick={handleClearFilters} data-id="element-1369">
                Clear Filters
              </Button>
            </div>}
        </div>
      </Card>

      {/* Content */}
      {isLoading ? <Card data-id="element-1370">
          <div className="p-12 text-center" data-id="element-1371">
            <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-1372"></div>
            <p className="mt-4 text-sm text-gray-500" data-id="element-1373">Loading invoices...</p>
          </div>
        </Card> : error ? <Card data-id="element-1374">
          <div className="p-12 text-center" data-id="element-1375">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-4" data-id="element-1376">
              <AlertCircleIcon className="w-6 h-6 text-red-600" data-id="element-1377" />
            </div>
            <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-1378">
              Failed to load invoices
            </p>
            <p className="text-sm text-gray-500 mb-4" data-id="element-1379">{error}</p>
            <Button size="sm" onClick={handleRefresh} data-id="element-1380">
              Retry
            </Button>
          </div>
        </Card> : <>
          <Table columns={columns} data={invoices} keyExtractor={inv => inv.id} emptyMessage="No invoices found" data-id="element-1381" />

          {/* Pagination */}
          {totalCount > 0 && <Card data-id="element-1382">
              <div className="flex items-center justify-between" data-id="element-1383">
                <p className="text-sm text-gray-600" data-id="element-1384">
                  Page {currentPage} of {totalPages || 1}
                </p>
                <div className="flex gap-2" data-id="element-1385">
                  <Button variant="secondary" size="sm" onClick={handlePrevPage} disabled={currentPage === 1} leftIcon={<ChevronLeftIcon className="w-4 h-4" data-id="element-1387" />} data-id="element-1386">
                    Previous
                  </Button>
                  <Button variant="secondary" size="sm" onClick={handleNextPage} disabled={!hasNextPage} rightIcon={<ChevronRightIcon className="w-4 h-4" data-id="element-1389" />} data-id="element-1388">
                    Next
                  </Button>
                </div>
              </div>
            </Card>}
        </>}

      {/* Share Modal */}
      <Modal isOpen={shareModalOpen} onClose={() => setShareModalOpen(false)} title="Share Invoice" data-id="element-1390">
        <div className="space-y-4" data-id="element-1391">
          <p className="text-sm text-gray-600" data-id="element-1392">
            Share this invoice with your customer using the link below:
          </p>

          {shareInvoiceLink && <div className="p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-1393">
              <code className="text-xs text-gray-700 break-all" data-id="element-1394">
                {shareInvoiceLink}
              </code>
            </div>}

          <div className="flex gap-3 pt-2" data-id="element-1395">
            <Button variant="primary" onClick={handleCopyLink} className="flex-1" data-id="element-1396">
              {copySuccess ? <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-1397">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-1398" />
                  </svg>
                  Copied!
                </> : <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-1399">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" data-id="element-1400" />
                  </svg>
                  Copy Link
                </>}
            </Button>

            <Button variant="secondary" onClick={handleViewLink} className="flex-1" data-id="element-1401">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-1402">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" data-id="element-1403" />
              </svg>
              View
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}